import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { DataDogLoggerService } from '../../../services/data-dog-logger.service';
import { TenantService } from '../../../services/tenant.service';
import { PASSWORD_PATTERN } from '../../models/login.model';
import { LoginService } from '../../services/login.service';

/**
 * Sign-up entry point.
 *
 * - For tenants with `login_provider === AUTH0` (today, only `agree` in dev)
 *   the actual registration form lives in Auth0 Universal Login
 *   (screen_hint=signup); this component only persists any `activation_token`
 *   and opens the Auth0 signup popup.
 * - Otherwise it falls back to the legacy registration form (POST /users).
 */
@Component({
  selector: 'app-sign-up',
  templateUrl: './sign-up.component.html',
  styleUrls: ['./sign-up.component.scss']
})
export class SignUpComponent implements OnInit, AfterViewInit, OnDestroy {

  /** The legacy form is rendered behind `*ngIf="!useAuth0"`, so it is not in the
   *  DOM on first render: use a non-static query and access it defensively. */
  @ViewChild(NgForm) public form: NgForm;

  public useAuth0 = false;
  /** True once the tenant has loaded and `useAuth0` reflects its real login_provider. */
  public tenantResolved = false;

  public availableLanguages: {
    name: string;
    slug: string;
  }[] = environment.languages;
  public sending = false;
  public loginError = false;
  public user = {
    email: null,
    name: null,
    last_name: null,
    password: null,
    password_confirmation: null,
    invitation: null
  };
  public with_mail: boolean;
  public PASSWORD_PATTERN = PASSWORD_PATTERN;

  private token: string;
  private captchaResponse: string;
  private activationToken: string | null = null;
  private destroy$ = new Subject<void>();

  /** @ignore */
  constructor(
    private router: Router,
    private loginService: LoginService,
    /** @ignore */
    public translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private tenantService: TenantService,
    private dataDogLoggerService: DataDogLoggerService
  ) { }

  /** @ignore */
  ngOnInit(): void {
    this.token = this.activatedRoute.snapshot.queryParams['activation_token'];

    this.tenantService.tenant$
      .pipe(filter(tenant => !!tenant), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        // El logout va aca adentro y no antes: si corre con el tenant sin
        // resolver, `usesAuth0` todavia lee LEGACY y la sesion de Auth0 queda
        // viva, con lo que el SSO devuelve al usuario a la sesion que acaba
        // de cerrar.
        this.loginService.logout(true);

        this.useAuth0 = this.loginService.usesAuth0;
        this.tenantResolved = true;

        if (this.useAuth0) {
          this.activationToken = this.token ?? null;
          if (this.activationToken) {
            // Persist the token so handleAuth0Callback can pick it up after the popup closes.
            sessionStorage.setItem('activation_token', this.activationToken);
          }
          return;
        }

        // Legacy registration form.
        if (this.token) {
          this.loginService.validateActivationToken(this.token).subscribe({
            next: user => {
              this.user.email = user.email;
              this.with_mail = true;
            },
            error: error => {
              this.dataDogLoggerService.warn(error.message, error.error);
            }
          });
        } else {
          this.with_mail = false;
        }
      });
  }

  /** @ignore */
  ngAfterViewInit(): void {
    // The legacy form only exists when `!useAuth0`, and useAuth0 itself is
    // resolved asynchronously once the tenant loads, so wait for the form to
    // actually appear instead of checking on a fixed timer.
    this.tenantService.tenant$
      .pipe(filter(() => this.tenantResolved && !this.useAuth0 && !!this.form), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        // Clear custom errors
        // TODO: directive or validator for custom errors?
        this.form.valueChanges.pipe(
          debounceTime(100),
          takeUntil(this.destroy$)
        ).subscribe(() => {
          // Clear all errors from the entire form
          this.form.control.setErrors(null);

          // Clear the 'unique' error specifically from the 'email' control if it exists
          const emailControl = this.form.controls['email'];
          if (emailControl?.errors?.['unique']) {
            emailControl.setErrors(null);
          }
        });
      });
  }

  /** @ignore */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Opens the Auth0 signup popup (dev). */
  public signUpWithAuth0(): void {
    this.sending = true;
    this.loginError = false;

    this.loginService.loginWithAuth0Popup('signup')
      .then(() => {
        this.router.navigateByUrl('companies?referral=signup');
      })
      .catch(err => {
        if (err?.message !== 'popup_closed') this.loginError = true;
        this.sending = false;
      });
  }

  /** Legacy sign-up form submit. */
  public signUp(): void {
    this.sending = true;

    if (this.token) {
      this.user.invitation = {
        activation_token: this.token
      };
    }

    this.loginService.register(this.user, this.captchaResponse).subscribe({
      next: login => {
        this.loginService.checkMailVerified();
        this.sending = false;
        this.router.navigate(['/companies']);
      },
      error: error => {
        this.sending = false;
        let message = error.error.message;
        if (message) {
          if (message.inputs && message.inputs.email && message.inputs.email.Unique) {

            this.form.controls.email.setErrors({
              "unique": true
            });

            this.form.control.setErrors({
              "not_unique_email": true
            });

          } else {

            this.form.control.setErrors({
              "remote": true
            });

          }
        } else {
          this.form.control.setErrors({
            "unknown": true
          });
        }
        this.dataDogLoggerService.warn(error.message, error.error);
      }
    });
  }

  /** Language switch. */
  public updateLanguage(slug: string): void {
    this.translateService.use(slug);
  }

  public resolved(captchaResponse: string): void {
    this.captchaResponse = captchaResponse;
  }
}
